// src/lib/api-queries.ts

import { ENTITY_MAPPING, LOADING_PHASES, ROLE_DATA_PATTERNS } from './EntityMapping.js'
import { logger } from './logger'

// Import from new modular API structure
import * as supabaseApi from './api'

// Debug: Check what's actually imported
// Debug logging removed to reduce console bloat

// Generic entity fetcher that matches your ProgressiveLoader.loadDataType
export const fetchEntity = async (entityName: string, companyId: string, options: { forceRefresh?: boolean } = {}) => {
  try {
    // Minimal logging - only log errors and warnings
    if (process.env.NODE_ENV === 'development' && options.forceRefresh && Math.random() < 0.1) {
      logger.api(`Force refreshing ${entityName}`)
    }
    
    // Ensure companyId is a string
    if (companyId && typeof companyId !== 'string') {
      console.warn(`[API Queries] companyId is not a string, converting:`, companyId)
      companyId = String(companyId)
    }
    
    const api = supabaseApi
    const entityConfig = ENTITY_MAPPING[entityName]
    
    if (!entityConfig) {
      console.warn(`[API Queries] No entity config found for ${entityName}`)
      return []
    }
    
    const method = entityConfig.apiMethod || `get${entityName.charAt(0).toUpperCase() + entityName.slice(1)}`
    
    // Minimal debug logging for critical entities (only in development)
    if (process.env.NODE_ENV === 'development' && ['company_branding'].includes(entityName) && Math.random() < 0.05) {
      logger.api(`${entityName} config:`, entityConfig)
    }
    
    if (!api[method]) {
      logger.api(`Method ${method} not found for ${entityName}, returning empty array`)
      return []
    }
    
    // Handle entities that don't require companyId
    const params = entityConfig.requiresCompanyId 
      ? [companyId, { forceRefresh: options.forceRefresh }] 
      : [{ forceRefresh: options.forceRefresh }]
    
    // Minimal debug logging removed to reduce console bloat
    
    const result = await api[method](...params)
    
    // Minimal debug logging removed to reduce console bloat
    
    if (result?.error && !result.data) {
      console.warn(`[API Queries] API call returned error for ${entityName}:`, result.error)
      return []
    }
    
    return result.data || []
  } catch (error) {
    console.error(`[API Queries] Error fetching ${entityName}:`, error)
    throw error
  }
}

// Essential data fetcher (replaces ProgressiveLoader essential phase)
export const fetchEssentialData = async (companyId: string) => {
  const essentialEntities = LOADING_PHASES.essential
  const results: Record<string, any[]> = {}
  
  logger.api(`Fetching essential data for company ${companyId}`)
  
  // Load all essential entities in parallel
  const promises = essentialEntities.map(async (entityName) => {
    try {
      const data = await fetchEntity(entityName, companyId)
      results[entityName] = data
      return { entityName, data, success: true }
    } catch (error) {
      console.error(`[API Queries] Failed to load ${entityName}:`, error)
      logger.warn(`[API Queries] Failed to load ${entityName}:`, error)
      results[entityName] = []
      return { entityName, data: [], success: false, error }
    }
  })
  
  await Promise.allSettled(promises)
  
  if (process.env.NODE_ENV === 'development') {
    logger.api(`Essential data loaded:`, Object.keys(results))
  }
  return results
}

// Background data fetcher (replaces ProgressiveLoader background phase)
export const fetchBackgroundData = async (companyId: string) => {
  const backgroundEntities = LOADING_PHASES.background
  const results: Record<string, any[]> = {}
  
  if (process.env.NODE_ENV === 'development') {
    logger.api(`Fetching background data for company ${companyId}`)
  }
  
  // Load background entities in smaller batches to be gentle on server
  const batchSize = 3
  for (let i = 0; i < backgroundEntities.length; i += batchSize) {
    const batch = backgroundEntities.slice(i, i + batchSize)
    
    const promises = batch.map(async (entityName) => {
      try {
        const data = await fetchEntity(entityName, companyId)
        results[entityName] = data
        return { entityName, data, success: true }
      } catch (error) {
        logger.warn(`[API Queries] Failed to load ${entityName}:`, error)
        results[entityName] = []
        return { entityName, data: [], success: false, error }
      }
    })
    
    await Promise.allSettled(promises)
    
    // Small delay between batches
    if (i + batchSize < backgroundEntities.length) {
      await new Promise(resolve => setTimeout(resolve, 200))
    }
  }
  
  if (process.env.NODE_ENV === 'development') {
    logger.api(`Background data loaded:`, Object.keys(results))
  }
  return results
}

// Role-based data fetcher (replaces BackgroundRefreshManager role patterns)
export const fetchRoleBasedData = async (companyId: string, role: string) => {
  const roleEntities = ROLE_DATA_PATTERNS[role] || []
  const results: Record<string, any[]> = {}
  
  if (process.env.NODE_ENV === 'development') {
    logger.api(`Fetching role-based data for ${role} at company ${companyId}`)
  }
  
  const promises = roleEntities.map(async (entityName) => {
    try {
      const data = await fetchEntity(entityName, companyId)
      results[entityName] = data
      return { entityName, data, success: true }
    } catch (error) {
      logger.warn(`[API Queries] Failed to load ${entityName}:`, error)
      results[entityName] = []
      return { entityName, data: [], success: false, error }
    }
  })
  
  await Promise.allSettled(promises)
  
  if (process.env.NODE_ENV === 'development') {
    logger.api(`Role-based data loaded for ${role}:`, Object.keys(results))
  }
  return results
}

// Cache warming fetcher (replaces CacheWarmer)
export const warmCache = async (companyId: string, userContext: { role?: string; timeOfDay?: string; season?: string }) => {
  const { role = 'owner', timeOfDay, season } = userContext
  
  if (process.env.NODE_ENV === 'development') {
    logger.api(`Warming cache for company ${companyId} with context:`, userContext)
  }
  
  // Get entities to warm based on role and patterns
  const entitiesToWarm = new Set<string>()
  
  // Add role-based entities
  const roleEntities = ROLE_DATA_PATTERNS[role] || []
  roleEntities.forEach(entity => entitiesToWarm.add(entity))
  
  // Add time-based entities if available
  if (timeOfDay) {
    const timeEntities = getTimeBasedEntities(timeOfDay)
    timeEntities.forEach(entity => entitiesToWarm.add(entity))
  }
  
  // Add seasonal entities if available
  if (season) {
    const seasonalEntities = getSeasonalEntities(season)
    seasonalEntities.forEach(entity => entitiesToWarm.add(entity))
  }
  
  // Always include core entities
  entitiesToWarm.add('clients')
  entitiesToWarm.add('services')
  entitiesToWarm.add('packages')
  
  const entities = Array.from(entitiesToWarm)
  const results: Record<string, any[]> = {}
  
  // Warm in batches
  const batchSize = 3
  for (let i = 0; i < entities.length; i += batchSize) {
    const batch = entities.slice(i, i + batchSize)
    
    const promises = batch.map(async (entityName) => {
      try {
        const data = await fetchEntity(entityName, companyId, { forceRefresh: true })
        results[entityName] = data
        return { entityName, data, success: true }
      } catch (error) {
        console.warn(`[API Queries] Failed to warm ${entityName}:`, error)
        results[entityName] = []
        return { entityName, data: [], success: false, error }
      }
    })
    
    await Promise.allSettled(promises)
    
    // Small delay between batches
    if (i + batchSize < entities.length) {
      await new Promise(resolve => setTimeout(resolve, 200))
    }
  }
  
  if (process.env.NODE_ENV === 'development') {
    logger.api(`Cache warming completed:`, Object.keys(results))
  }
  return results
}

// Helper functions for time and seasonal patterns
const getTimeBasedEntities = (timeOfDay: string) => {
  const patterns: Record<string, string[]> = {
    morning: ['estimates', 'calendar_events', 'jobs', 'tasks', 'clients'],
    afternoon: ['jobs', 'invoices', 'payments', 'messages', 'job_activity'],
    evening: ['reports', 'audit_logs', 'expenses', 'automations', 'ai_conversations']
  }
  return patterns[timeOfDay] || []
}

const getSeasonalEntities = (season: string) => {
  const patterns: Record<string, string[]> = {
    spring: ['estimates', 'jobs', 'clients', 'services'],
    summer: ['jobs', 'invoices', 'payments', 'job_activity'],
    fall: ['invoices', 'payments', 'expenses', 'audit_logs'],
    winter: ['reports', 'automations', 'ai_conversations', 'company_branding']
  }
  return patterns[season] || []
}

// Individual entity refresh (replaces ProgressiveLoader.reloadDataType)
export const refreshEntity = async (entityName: string, companyId: string) => {
  logger.api(`Refreshing ${entityName} for company ${companyId}`)
  return fetchEntity(entityName, companyId, { forceRefresh: true })
}

// All company data fetcher (for complete refresh)
export const fetchAllCompanyData = async (companyId: string) => {
  logger.api(`Fetching all company data for ${companyId}`)
  
  const [essentialData, backgroundData] = await Promise.all([
    fetchEssentialData(companyId),
    fetchBackgroundData(companyId)
  ])
  
  return {
    ...essentialData,
    ...backgroundData
  }
}